import { FormGroup, InputGroup, SelectInput } from '@allurion/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { TrackedButton } from 'src/analytics/TrackedUI';
import { Loader } from 'src/components/ui/Loader';
import { PatientTreatment } from 'src/domain/patient/Patients';
import { isValidStartDate } from 'src/domain/patient/treatment';
import { useTreatmentLabels } from 'src/hooks/useTreatmentLabels';
import globalMessages from 'src/messages/global.messages';

import translations from './PatientTreamentsCard.translations';

import styles from './TreatmentForm.module.scss';

type Props = {
  treatment: PatientTreatment;
  onSave: (t: PatientTreatment) => void;
  onCancel: (t: PatientTreatment) => void;
  treatmentsOffered?: number[] | string[];
  clinicId: string;
};

export function TreatmentForm({ clinicId, treatment, onSave, onCancel, treatmentsOffered }: Props) {
  const intl = useIntl();
  const [isSaving, setIsSaving] = useState(false);
  const { getTreatmentTypeLabel } = useTreatmentLabels(clinicId);

  const treatmentsOptions =
    treatmentsOffered?.map((treatmentId) => ({
      label: getTreatmentTypeLabel(treatmentId),
      value: treatmentId,
    })) ?? [];

  const initialValues: PatientTreatment = {
    TreatmentTypeID: 0,
    TreatmentUserID: 0,
    StartDate: '',
    EndDate: undefined,
    TreatmentTypeDefinition: '',
    CustomTreatmentTypeName: '',
  };

  const TreatmentSchema = Yup.object().shape({
    TreatmentTypeID: Yup.number()
      .moreThan(0, intl.formatMessage(globalMessages.required))
      .required(intl.formatMessage(globalMessages.required)),
    StartDate: Yup.string().required(intl.formatMessage(globalMessages.required)),
    EndDate: Yup.string().nullable(),
  });

  function isValidEndDate(endDateStr: string | undefined, startDateStr: string | undefined) {
    return !endDateStr || !startDateStr || endDateStr >= startDateStr;
  }

  const handleCancel = () => {
    onCancel(treatment);
  };

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<PatientTreatment>({
    resolver: yupResolver(TreatmentSchema),
    defaultValues: treatment || initialValues,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <Loader isLoading={isSaving} />

      <div className={styles.content}>
        <FormGroup
          label={intl.formatMessage(translations.treatmentType)}
          error={errors.TreatmentTypeID?.message}
        >
          <SelectInput {...register('TreatmentTypeID')} options={treatmentsOptions} />
        </FormGroup>

        <FormGroup
          label={intl.formatMessage(translations.treatmentStartDate)}
          error={errors.StartDate?.message}
        >
          <InputGroup textInput={{ type: 'date', ...register('StartDate') }} />
        </FormGroup>

        <FormGroup
          label={intl.formatMessage(translations.treatmentEndDate)}
          error={errors.EndDate?.message}
        >
          <InputGroup textInput={{ type: 'date', ...register('EndDate') }} />
        </FormGroup>
      </div>

      <div className={styles.footer}>
        <TrackedButton
          label={intl.formatMessage(globalMessages.cancel)}
          type="button"
          onClick={handleCancel}
          variant="secondary"
          trackLabel="cancel-treatment-form"
        />
        <TrackedButton
          label={intl.formatMessage(translations.treatmentSave)}
          type="submit"
          trackLabel="submit-treatment-form"
        />
      </div>
    </form>
  );

  async function onSubmit(treatment: PatientTreatment) {
    if (treatment.StartDate && !isValidStartDate(treatment.StartDate)) {
      setError('StartDate', {
        type: 'custom',
        message: intl.formatMessage(translations.startDateErrorMessage),
      });

      return false;
    }

    if (treatment.EndDate && !isValidEndDate(treatment.EndDate, treatment.StartDate)) {
      setError('EndDate', {
        type: 'custom',
        message: intl.formatMessage(translations.endDateErrorMessage),
      });

      return false;
    }

    setIsSaving(true);

    await onSave(treatment);

    setIsSaving(false);
  }
}
